import React, { useState, useEffect } from 'react';
import { api, isDev } from '../../../services/api';

interface Personnel {
  id: number | string;
  nom: string;
  specialite: 'coupeur' | 'tailleur' | 'brodeur';
  tarif_par_coupe: number;
  tarif_par_couture: number;
  tarif_par_broderie: number;
  tarifs_json: any;
}

interface AssignmentModalProps {
  commandeIds: number[];
  type: 'COUPE' | 'COUTURE' | 'BRODERIE';
  onClose: () => void;
  onSuccess: () => void;
  modeleTypeId?: number;
}

const PREDEFINED_TASKS = {
  COUPE: ['Coupe complète', 'Découpage pièces', 'Marquage'],
  COUTURE: ['Habit (Haut)', 'Pantalon (Bas)', 'Chapeau', 'Complet', 'Finition'],
  BRODERIE: ['Motif Dos', 'Motif Col', 'Motif Poignets', 'Broderie complète'],
};

interface AssignmentDetail {
  personnelId: number | string;
  nom: string;
  tasks: string[];
  customTask: string;
  cost: number;
}

export const AssignmentModal: React.FC<AssignmentModalProps> = ({ 
  commandeIds, 
  type, 
  onClose, 
  onSuccess,
  modeleTypeId 
}) => {
  const [personnel, setPersonnel] = useState<Personnel[]>([]);
  const [loading, setLoading] = useState(true);
  const [assignments, setAssignments] = useState<AssignmentDetail[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Specific order customizations
  const [customizations, setCustomizations] = useState<Record<number, AssignmentDetail[]>>({});
  const [editingOrderId, setEditingOrderId] = useState<number | null>(null);

  useEffect(() => {
    fetchPersonnel();
  }, []);

  const fetchPersonnel = async () => {
    try {
      const token = localStorage.getItem('atelier_bassoum_token');
      if (!token) return;
      const data = await api.getPersonnel(token);
      const filtered = data.filter((p: any) => {
        if (type === 'COUPE') return p.specialite === 'coupeur';
        if (type === 'COUTURE') return p.specialite === 'tailleur';
        if (type === 'BRODERIE') return p.specialite === 'brodeur';
        return false;
      });
      setPersonnel(filtered);
    } catch (err) {
      setError("Erreur lors de la récupération du personnel.");
    } finally {
      setLoading(false);
    }
  };

  const calculateDefaultCost = (personId: number | string) => {
    const person = personnel.find(p => p.id === personId);
    if (!person) return 0;
    let baseCost = 0;
    if (type === 'COUPE') baseCost = person.tarif_par_coupe;
    else if (type === 'COUTURE') baseCost = person.tarif_par_couture;
    else if (type === 'BRODERIE') baseCost = person.tarif_par_broderie;

    if (modeleTypeId && person.tarifs_json) {
      const specific = person.tarifs_json.find((t: any) => String(t.typeId) === String(modeleTypeId));
      if (specific) baseCost = specific.prix;
    }
    return baseCost;
  };

  const togglePerson = (p: Personnel) => {
    setAssignments(prev => {
      const exists = prev.find(a => a.personnelId === p.id);
      if (exists) {
        return prev.filter(a => a.personnelId !== p.id);
      } else {
        return [...prev, {
          personnelId: p.id,
          nom: p.nom,
          tasks: [],
          customTask: '',
          cost: calculateDefaultCost(p.id)
        }];
      }
    });
  };

  const togglePersonSpecific = (p: Personnel, orderId: number) => {
    setCustomizations(prev => {
      const current = prev[orderId] || assignments.map(a => ({...a}));
      const exists = current.find(a => a.personnelId === p.id);
      let next;
      if (exists) {
        next = current.filter(a => a.personnelId !== p.id);
      } else {
        next = [...current, {
          personnelId: p.id,
          nom: p.nom,
          tasks: [],
          customTask: '',
          cost: calculateDefaultCost(p.id)
        }];
      }
      return { ...prev, [orderId]: next };
    });
  };

  const updateAssignment = (personnelId: number | string, updates: Partial<AssignmentDetail>, orderId?: number) => {
    if (orderId) {
      setCustomizations(prev => {
        const current = prev[orderId] || assignments.map(a => ({...a}));
        const next = current.map(a => a.personnelId === personnelId ? { ...a, ...updates } : a);
        return { ...prev, [orderId]: next };
      });
    } else {
      setAssignments(prev => prev.map(a => a.personnelId === personnelId ? { ...a, ...updates } : a));
    }
  };

  const toggleTaskForPerson = (personnelId: number | string, task: string, orderId?: number) => {
    const list = orderId ? (customizations[orderId] || assignments.map(a => ({...a}))) : assignments;
    const assign = list.find(a => a.personnelId === personnelId);
    if (!assign) return;
    const newTasks = assign.tasks.includes(task) 
      ? assign.tasks.filter(t => t !== task) 
      : [...assign.tasks, task];
    updateAssignment(personnelId, { tasks: newTasks }, orderId);
  };

  const handleSave = async () => {
    if (assignments.length === 0) {
      setError("Veuillez sélectionner au moins un personnel.");
      return;
    }

    // Validation
    for (const a of assignments) {
      if (a.tasks.length === 0 && !a.customTask.trim()) {
        setError(`Veuillez spécifier une tâche pour ${a.nom}.`);
        return;
      }
    }

    setSaving(true);
    setError(null);
    try {
      const token = localStorage.getItem('atelier_bassoum_token');
      if (!token) return;

      for (const cmdId of commandeIds) {
        const currentAssignments = customizations[cmdId] || assignments;
        
        if (currentAssignments.length === 0) continue;

        for (const a of currentAssignments) {
          const payload = {
            personnel_id: a.personnelId,
            type,
            tache: [...a.tasks, a.customTask].filter(Boolean).join(', '),
            cout_main_doeuvre: a.cost,
            statut: 'EN_ATTENTE'
          };
          await api.createAffectation(token, String(cmdId), payload);
        }
      }

      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.message || "Erreur lors de l'assignation.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-2 sm:p-4 md:p-6 overflow-hidden">
      <div className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm" onClick={onClose} />
      
      <div className="relative bg-gray-50 w-full max-w-2xl max-h-[98vh] flex flex-col rounded-[2.5rem] shadow-2xl overflow-hidden animate-in zoom-in duration-300">
        {/* Header */}
        <div className="px-8 py-6 bg-white border-b border-gray-100 flex items-center justify-between z-10">
          <div>
            <h3 className="font-playfair font-bold text-2xl text-gray-900">
              Assignation : {type} {commandeIds.length > 1 ? `(${commandeIds.length} commandes)` : ''}
            </h3>
            <p className="text-[10px] text-gray-400 mt-1 uppercase tracking-[0.2em] font-black">Configuration granulaire par personnel</p>
          </div>
          <button onClick={onClose} title="Fermer" className="w-11 h-11 flex items-center justify-center rounded-2xl bg-gray-50 hover:bg-red-50 text-gray-400 hover:text-red-500 transition-all active:scale-95">
            <i className="ri-close-line text-2xl" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-8 custom-scrollbar">
          {error && (
            <div className="mx-2 p-4 bg-red-50 border-2 border-red-100 rounded-3xl text-red-600 text-xs font-bold flex items-start gap-3 animate-shake">
              <i className="ri-error-warning-fill text-xl" />
              <p className="flex-1 leading-relaxed">{error}</p>
            </div>
          )}

          {/* Personnel Selection */}
          <section className="space-y-4">
            <div className="flex justify-between items-end px-3">
               <label className="text-[11px] font-black text-gray-400 uppercase tracking-widest">1. Choisir l'équipe</label>
               <span className="text-[10px] font-black text-gray-300 italic">{personnel.length} disponible(s)</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {loading ? (
                [1,2,3].map(i => <div key={i} className="h-20 bg-white rounded-3xl animate-pulse" />)
              ) : (
                personnel.map(p => {
                  const isSelected = assignments.some(a => a.personnelId === p.id);
                  return (
                    <button
                      key={p.id}
                      onClick={() => togglePerson(p)}
                      className={`relative p-3 rounded-3xl border-2 transition-all text-center group flex flex-col items-center gap-2 ${
                        isSelected 
                          ? 'border-foret-500 bg-white shadow-xl shadow-foret-100' 
                          : 'border-transparent bg-white hover:bg-gray-50'
                      }`}
                    >
                      <div className={`w-12 h-12 rounded-2xl flex items-center justify-center text-lg font-black transition-all ${
                         isSelected ? 'bg-foret-500 text-white scale-110' : 'bg-gray-100 text-gray-400'
                      }`}>
                        {p.nom.charAt(0)}
                      </div>
                      <div className="min-w-0 w-full">
                         <p className={`font-black text-[11px] truncate ${isSelected ? 'text-foret-800' : 'text-gray-700'}`}>{p.nom}</p>
                      </div>
                      {isSelected && (
                        <div className="absolute -top-2 -right-2 w-6 h-6 bg-foret-500 rounded-full flex items-center justify-center text-white text-xs border-4 border-white shadow-lg">
                          <i className="ri-check-line font-bold" />
                        </div>
                      )}
                    </button>
                  );
                })
              )}
            </div>
          </section>

          {/* Details Configuration (Global) */}
          {assignments.length > 0 && !editingOrderId && (
            <section className="space-y-6">
              <label className="text-[11px] font-black text-gray-400 uppercase tracking-widest px-3">2. Configurer la base commune</label>
              <div className="space-y-4">
                {assignments.map(a => (
                  <div key={a.personnelId} className="bg-white rounded-[2rem] border-2 border-gray-100 p-6 space-y-6 shadow-sm hover:shadow-md transition-all">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-foret-50 text-foret-600 flex items-center justify-center font-black">
                          {a.nom.charAt(0)}
                        </div>
                        <h4 className="font-black text-gray-800">{a.nom}</h4>
                      </div>
                      <button 
                        onClick={() => togglePerson(personnel.find(p => p.id === a.personnelId)!)}
                        title="Retirer ce personnel"
                        className="text-gray-300 hover:text-red-500 transition-colors"
                      >
                        <i className="ri-delete-bin-line text-lg" />
                      </button>
                    </div>

                    <div className="space-y-3">
                      <p className="text-[9px] font-black text-gray-400 uppercase tracking-wider pl-1">Tâches spécifiques</p>
                      <div className="flex flex-wrap gap-2">
                        {PREDEFINED_TASKS[type].map(task => (
                          <button
                            key={task}
                            onClick={() => toggleTaskForPerson(a.personnelId, task)}
                            className={`px-4 py-2 rounded-xl text-[10px] font-black border-2 transition-all ${
                              a.tasks.includes(task)
                                ? 'bg-foret-600 border-foret-600 text-white shadow-lg shadow-foret-100'
                                : 'bg-gray-50 border-transparent text-gray-500 hover:bg-gray-100'
                            }`}
                          >
                            {task}
                          </button>
                        ))}
                      </div>
                      <input 
                        type="text"
                        placeholder="Autre tâche personnalisée..."
                        value={a.customTask}
                        onChange={(e) => updateAssignment(a.personnelId, { customTask: e.target.value })}
                        className="w-full bg-gray-50 border-transparent focus:border-foret-200 focus:bg-white rounded-xl px-4 py-3 text-xs font-bold transition-all"
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-center">
                      <p className="text-[9px] font-black text-gray-400 uppercase tracking-wider pl-1">Coût main d'œuvre</p>
                      <div className="relative group">
                        <input 
                          type="number"
                          title="Coût de la main d'œuvre"
                          value={a.cost}
                          onChange={(e) => updateAssignment(a.personnelId, { cost: Number(e.target.value) })}
                          className="w-full bg-gray-50 border-2 border-transparent focus:border-foret-500 focus:bg-white rounded-2xl px-12 py-3 text-xl font-black text-gray-800 outline-none transition-all text-right"
                        />
                        <div className="absolute left-4 top-1/2 -translate-y-1/2 text-foret-400">
                          <i className="ri-money-cfa-circle-line text-2xl" />
                        </div>
                        <div className="absolute right-4 top-1/2 -translate-y-1/2 text-[9px] font-black text-gray-300">FCFA</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Batch Customization List */}
          {commandeIds.length > 1 && assignments.length > 0 && !editingOrderId && (
            <section className="space-y-4 pt-4 border-t border-gray-100">
               <div className="flex justify-between items-center px-3">
                  <label className="text-[11px] font-black text-gray-400 uppercase tracking-widest">3. Ajustements par commande (Optionnel)</label>
                  <span className="bg-amber-100 text-amber-700 text-[9px] font-black px-2 py-0.5 rounded-full uppercase">Optionnel</span>
               </div>
               <div className="grid grid-cols-1 gap-2">
                  {commandeIds.map(id => (
                    <div key={id} className="bg-white rounded-3xl p-4 flex items-center justify-between border border-gray-100 hover:border-amber-200 transition-all">
                       <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-xl bg-gray-50 flex items-center justify-center font-black text-[10px] text-gray-400">#{id}</div>
                          <span className="text-xs font-black text-gray-600">Commande #{id}</span>
                          {customizations[id] && <span className="w-2 h-2 rounded-full bg-amber-500" title="Personnalisé" />}
                       </div>
                       <button 
                         onClick={() => setEditingOrderId(id)}
                         className="px-4 py-2 rounded-xl text-[10px] font-black text-amber-600 hover:bg-amber-50 transition-all uppercase"
                       >
                         {customizations[id] ? 'Modifier' : 'Ajuster'}
                       </button>
                    </div>
                  ))}
               </div>
            </section>
          )}

          {/* Specific Customization View */}
          {editingOrderId && (
            <section className="space-y-6 animate-in slide-in-from-right duration-300 pb-10">
               <div className="flex items-center gap-3 px-3">
                  <button onClick={() => setEditingOrderId(null)} className="w-10 h-10 rounded-2xl bg-gray-100 flex items-center justify-center text-gray-500 hover:bg-gray-200 active:scale-90 transition-all">
                     <i className="ri-arrow-left-line text-xl" />
                  </button>
                  <div>
                    <label className="text-[11px] font-black text-gray-900 uppercase tracking-widest block">
                      Ajustement : Commande #{editingOrderId}
                    </label>
                    <p className="text-[9px] text-gray-400 uppercase font-bold">Configuration spécifique</p>
                  </div>
               </div>

               <div className="bg-amber-50 border-2 border-amber-100 rounded-3xl p-5">
                  <p className="text-[10px] text-amber-700 font-bold leading-relaxed">
                    Vous modifiez l'équipe spécifiquement pour cette commande. Les changements ici n'affecteront pas les autres commandes du lot.
                  </p>
               </div>

               {/* Personnel Selection for Specific Order */}
               <div className="flex flex-wrap gap-2 px-3">
                  {personnel.map(p => {
                    const currentList = customizations[editingOrderId!] || assignments.map(a => ({...a}));
                    const isSelected = currentList.some(a => a.personnelId === p.id);
                    return (
                      <button
                        key={p.id}
                        onClick={() => togglePersonSpecific(p, editingOrderId!)}
                        className={`px-3 py-2 rounded-xl text-[10px] font-black border-2 transition-all ${
                          isSelected ? 'bg-amber-500 border-amber-500 text-white' : 'bg-white border-gray-100 text-gray-400'
                        }`}
                      >
                        {p.nom}
                      </button>
                    );
                  })}
               </div>

               {/* Reuse forms for specific order */}
               <div className="space-y-4">
                  {(customizations[editingOrderId] || assignments.map(a => ({...a}))).map(a => (
                    <div key={a.personnelId} className="bg-white rounded-[2rem] border-2 border-amber-200 p-6 space-y-6 shadow-sm">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center font-black">
                            {a.nom.charAt(0)}
                          </div>
                          <h4 className="font-black text-gray-800">{a.nom}</h4>
                        </div>
                      </div>

                      <div className="space-y-3">
                        <p className="text-[9px] font-black text-gray-400 uppercase tracking-wider pl-1">Tâches spécifiques (Commande #{editingOrderId})</p>
                        <div className="flex flex-wrap gap-2">
                          {PREDEFINED_TASKS[type].map(task => (
                            <button
                              key={task}
                              onClick={() => toggleTaskForPerson(a.personnelId, task, editingOrderId!)}
                              className={`px-4 py-2 rounded-xl text-[10px] font-black border-2 transition-all ${
                                a.tasks.includes(task)
                                  ? 'bg-amber-600 border-amber-600 text-white shadow-lg shadow-amber-100'
                                  : 'bg-gray-50 border-transparent text-gray-500 hover:bg-gray-100'
                              }`}
                            >
                              {task}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-center">
                        <p className="text-[9px] font-black text-gray-400 uppercase tracking-wider pl-1">Tarif spécifique</p>
                        <div className="relative group">
                          <input 
                            type="number"
                            title="Coût de la main d'œuvre spécifique"
                            value={a.cost}
                            onChange={(e) => updateAssignment(a.personnelId, { cost: Number(e.target.value) }, editingOrderId!)}
                            className="w-full bg-white border-2 border-amber-500 rounded-2xl px-12 py-3 text-xl font-black text-gray-800 outline-none text-right shadow-sm"
                          />
                          <div className="absolute left-4 top-1/2 -translate-y-1/2 text-amber-400">
                            <i className="ri-money-cfa-circle-line text-2xl" />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
               </div>

               <button 
                 onClick={() => setEditingOrderId(null)}
                 className="w-full py-5 bg-gray-900 text-white rounded-[1.5rem] text-xs font-black uppercase tracking-widest shadow-xl active:scale-95 transition-all"
               >
                 Enregistrer les ajustements pour #{editingOrderId}
               </button>
            </section>
          )}

          {assignments.length === 0 && !editingOrderId && (
            <div className="py-20 text-center space-y-4">
               <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto shadow-inner">
                  <i className="ri-team-line text-4xl text-gray-200" />
               </div>
               <p className="text-gray-400 font-bold text-sm">Veuillez sélectionner au moins une personne pour commencer la configuration</p>
            </div>
          )}
        </div>

        {/* Footer */}
        {!editingOrderId && (
          <div className="p-8 bg-white border-t border-gray-100 flex flex-col sm:flex-row gap-4">
            <button
              onClick={onClose}
              className="flex-1 py-4 text-gray-400 font-black text-xs uppercase tracking-[0.2em] hover:text-gray-600 transition-all"
            >
              Annuler
            </button>
            <button
              onClick={handleSave}
              disabled={saving || assignments.length === 0}
              className="flex-[2] py-5 bg-foret-500 text-white font-black rounded-[1.5rem] hover:bg-foret-600 transition-all shadow-2xl shadow-foret-500/20 disabled:opacity-20 disabled:grayscale disabled:cursor-not-allowed cursor-pointer flex items-center justify-center gap-4 active:scale-[0.98]"
            >
              {saving ? (
                <i className="ri-loader-4-line animate-spin text-2xl" />
              ) : (
                <i className="ri-checkbox-circle-line text-2xl" />
              )}
              <span className="uppercase tracking-[0.1em] text-xs">
                {saving ? 'Traitement...' : commandeIds.length > 1 ? `Assigner à ${commandeIds.length} commandes` : 'Valider les assignations'}
              </span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
